import {
  bridge,
  coreEngine,
  Entity,
  events,
  properties,
} from "@dinahmoe/dmafjs-core";

class VisualSuper extends Entity {
  userEventTypes = [
    "down",
    "move",
    "up",
    "gesture",
    "click",
    "double_click",
    "over",
    "out",
    "leave",
    "scroll",
    "drag",
    "drop",
    "intersection",
    "focus",
    "unfocus",
  ];

  engine = "visual";
  namespace = "visual";
  handlers = {};

  init(cNode, event) {
    cNode._hasDynamicWidth = this._isDynamicValue(cNode, "width");
    cNode._hasDynamicHeight = this._isDynamicValue(cNode, "height");

    this._setupEvents(cNode);

    bridge.create({ ...this.getInitData(cNode), uid: "visual/" + cNode.uid, sender: "core" });
  }

  _setupEvents(cNode) {
    const userEventTypesFromConfig = events
      .getEventNamesForUid(cNode.uid)
      .filter((eventType) => this.userEventTypes.includes(eventType));

    if ((!cNode.events || cNode.events.length === 0) && userEventTypesFromConfig.length === 0) {
      return;
    }

    if (!cNode.events) cNode.events = [];

    cNode._dispatchConfigs = {};

    for (const entry of cNode.events) {
      if (entry.name || entry.routeId) {
        let customName = entry.name;
        if (entry.routeId && entry.navigationUid) {
          customName = `${entry.navigationUid}:navigate:${entry.routeId}`;
        }

        cNode._dispatchConfigs[entry.type] = {
          customName,
          customTime: entry.time || 0,
          customData: entry.data || {},
          routeId: entry.routeId,
          navigationUid: entry.navigationUid
        };
      }
    }

    // Add action-config events to events array and mark as dispatchable
    const existingTypes = new Set(cNode.events.map(e => e.type));
    for (const eventType of userEventTypesFromConfig) {
      if (!existingTypes.has(eventType)) {
        cNode.events.push({ type: eventType });
      }
      if (!cNode._dispatchConfigs[eventType]) {
        cNode._dispatchConfigs[eventType] = {};
      }
    }
  }

  receiveMessage(cNode, message) {
    this._doDispatch(cNode, message);

    switch (message.key) {
      case "resize": {
        this._envResize(cNode, message);
        break;
      }
      case "move": {
        this._envMove(cNode, message);
        break;
      }
      case "scroll-smooth": {
        this.receiveSmoothScrollEvent(cNode, message);
        break;
      }
    }
  }

  _doDispatch(cNode, message) {
    const config = cNode._dispatchConfigs?.[message.key];
    if (!config) return;

    let eventName;
    let eventTime = message.time;
    let eventValue = message.value;

    if (config.customName) {
      eventName = config.customName;

      if (config.customTime) {
        eventTime += config.customTime;
      }

      eventValue = {
        ...message.value,
        ...config.customData
      };
    } else {
      eventName = `${cNode.uid}:${message.key}`;
    }

    this.dispatch(eventName, eventTime, eventValue);
  }

  _isDynamicValue(cNode, dimension) {
    if (dimension === "width") {
      if (cNode.props["size.widthAuto"]) return true;
    } else {
      if (cNode.props["size.heightAuto"]) return true;
    }
    const value = dimension === "width" ? cNode.props["size.width"] : cNode.props["size.height"];
    return typeof value === "string" && value.includes("%");
  }

  _envResize(cNode, message) {
    // Ignore forwarded child resize messages — only apply resize from own element
    if (message.uid !== cNode.uid) return;

    if (cNode._hasDynamicHeight && !properties.isLinked(cNode.uid, "size.height")) {
      cNode["size.height"] = message.value.scrollHeight;
    }
    if (cNode._hasDynamicWidth && !properties.isLinked(cNode.uid, "size.width")) {
      cNode["size.width"] = message.value.scrollWidth;
    }
  }

  _envMove(cNode, message) {
    cNode["pointer.x"] = message.value.x;
    cNode["pointer.pageX"] = message.value.pageX;
    cNode["pointer.y"] = message.value.y;
    cNode["pointer.pageY"] = message.value.pageY;
  }

  receiveSmoothScrollEvent(cNode, message) {
    switch (message.value.scrollType) {
      case "wheel": {
        cNode["pointer.deltaX"] = message.value.deltaX;
        cNode["pointer.deltaY"] = message.value.deltaY;
        break;
      }
      case "touchStart": {
        cNode.touchStartX = message.value.touchStartX;
        cNode.touchStartY = message.value.touchStartY;
        break;
      }
      case "touchMove": {
        const { touchX, touchY } = message.value;
        // TODO touchMultiplier doesn't seem to be used
        // and why are we doing this on the core side?
        // it doesn't look like it is correct
        cNode["pointer.deltaX"] = -(touchX - cNode.touchStartX) * cNode["pointer.touchMultiplier"];
        cNode["pointer.deltaY"] = -(touchY - cNode.touchStartY) * cNode["pointer.touchMultiplier"];
        cNode.touchStartX = touchX;
        cNode.touchStartY = touchY;
        break;
      }
    }
  }

  setWidth(node) {
    let value = node["size.width"];
    if (value === undefined) return;
    if (node["size.widthMax"] !== undefined) {
      value = Math.min(value, node["size.widthMax"]);
    }
    if (node["size.widthMin"] !== undefined) {
      value = Math.max(value, node["size.widthMin"]);
    }
    node.props["size.width"] = value;
  }

  setHeight(node) {
    let value = node["size.height"];
    if (value === undefined) return;
    if (node["size.heightMax"] !== undefined) {
      value = Math.min(value, node["size.heightMax"]);
    }
    if (node["size.heightMin"] !== undefined) {
      value = Math.max(value, node["size.heightMin"]);
    }
    node.props["size.height"] = value;
  }

  focus(node) {
    bridge.update({
      uid: "visual/" + node.uid,
      key: "focus",
      sender: "core",
    });
  }
}

const visualSuper = new VisualSuper();
export { VisualSuper, visualSuper };
